import { Functional, BoundingBox } from '@wayfinder/vms-base';
import * as R from 'ramda';
import { actions, LOADING_STATUS_FAILED, LOADING_STATUS_LOADED, LOADING_STATUS_LOADING } from './constants';
import { checkSpritesForVenue } from './landscape-validation';
import * as LokiDb from './loki-db';

const emptyState = () => ({
    loadingStatus: {
        features: {},
        entities: {},
        themes: {},
        badges: {},
        map: undefined,
        mapError: undefined,
        overallVenue: undefined,
        overallVenueError: undefined,
    },

    structures: {},

    theme: undefined,
    lastChangedAt: 0,
    mapStyle: null,
    isEntityMigrated: false,
    isFeatureMigrated: false,
});

/*
 * Update the state from a slew of data
 * @sig handleSetMetadata :: (Object, { metadata: [Venue, [Structure], [Level], [Ordinal]] }}) -> Object
 *   OrdinalLookup   = { [OrdinalId]:   Ordinal }
 *   StructureLookup = { [StructureId]: Structure }
 *   LevelLookup     = { [LevelId]:     Level }
 *   TODO: remove venue from loki and update comments
 */

const arrayToLookup = Functional.arrayToLookupTable('id');

const handleSetVenue = (state, action) => {
    state = Functional.mergeWithProp('venue', arrayToLookup([action.venue]), state);
    return markStateChanged(state);
};

const handleSetOrdinals = (state, action) => {
    state = Functional.mergeWithProp('ordinals', arrayToLookup(action.ordinals), state);
    return markStateChanged(state);
};

const handleSetStructures = (state, action) => {
    state = Functional.mergeWithProp('structures', arrayToLookup(action.structures), state);
    return markStateChanged(state);
};

const handleSetLevels = (state, action) => {
    state = Functional.mergeWithProp('levels', arrayToLookup(action.levels), state);
    return markStateChanged(state);
};

/*
 * Reset everything to the emptyState
 * @sig handleReset :: () -> Object
 */
const handleReset = (/* state, action */) => {
    LokiDb.reset();
    return emptyState();
};

/*
 * Remove a Feature and then remove its id from the Ordinal (or background), Structure, Level and Venue
 * @sig handleRemoveFeature :: (State, { Feature }) -> State
 */
const handleRemoveFeature = (state, action) => {
    LokiDb.removeFeature(action.feature);
    return markStateChanged(state);
};

/*
 * Change the loadingStatus at 'path' to value, and if present, the value at errorPath to error
 * @sig loading :: (State, [String], String, [String], Error) -> State
 */
const loading = (state, path, value, errorPath = undefined, error = undefined) => {
    if (errorPath) {
        state = R.assocPath(R.prepend('loadingStatus', errorPath), error.message, state);
        // eslint-disable-next-line no-console
        console.error(`LandscapeLoader: loading failed for ${path}. ${error}`);
    }

    state = R.assocPath(R.prepend('loadingStatus', path), value, state);
    return state;
};

const handleMapStyle = (state, action) => {
    const updatedState = R.mergeRight(state, { mapStyle: action.mapStyle });
    return markStateChanged(updatedState);
};

const handleSprites = (state, action) => R.mergeRight(state, { sprites: action.sprites });

const handleVenueDropdown = (state, action) => {
    const venueDropdowns = R.map(v => ({ value: v.id, label: v.id }), action.venueSummaries);
    return R.assoc('venueDropdowns', venueDropdowns, state);
};

/*
 * Get the coordinates of the polygon that is the basemap feature
 * Then convert it from LngLat coordinates to LatLng
 * Turn the BoundsPolygon into a BoundingBox
 * @sig extractBoundsFromFeature :: Feature -> BoundingBox
 */
const extractBoundsFromFeature = basemapFeature => {
    const coordinates = R.pathOr([], ['geometry', 'coordinates', 0], basemapFeature);
    const boundsPolygon = R.map(R.reverse, coordinates); // change from LngLat to LatLng
    return BoundingBox.fromBoundsPolygon(boundsPolygon);
};

/*
 * https://app.asana.com/0/873618806933910/1152873822875201
 * Extract the 'basemap' feature from the background geojson features
 * Turn it into a BoundingBox which we'll use to clamp the viewbox, limiting the area a user can pan the map.
 * If there is no basemap feature with the category 'concrete' / 'basemap' it probably needs to be re categorized
 * in the AiJson. If we can't find one, use the venue's bounding box to clamp
 * @sig handleSetBoundingBox :: (State, Action) -> State
 */
const handleSetBoundingBox = (state, action) => {
    const basemapFeature = R.find(
        f => f.properties.category === 'concrete' || f.properties.category === 'basemap',
        action.features,
    );
    const boundingBox = basemapFeature
        ? extractBoundsFromFeature(basemapFeature)
        : R.path(['venue', action.venueId, 'boundingBox'], state);
    return Functional.mergeWithProp('boundingBox', boundingBox, state);
};

const handleSetIsMigrated = (state, action) => {
    const isEntityMigrated = R.pathOr(false, ['isMigrated', 'isEntityMigrated'], action);
    const isFeatureMigrated = R.pathOr(false, ['isMigrated', 'isFeatureMigrated'], action);

    const setMigrations = R.pipe(
        R.assoc('isEntityMigrated', isEntityMigrated),
        R.assoc('isFeatureMigrated', isFeatureMigrated),
    );

    return setMigrations(state);
};

/*
 * Mark the state changed by incrementing a counter (because otherwise changes to Loki won't trigger a redux change)
 * @sig markStateChanged :: State -> State
 */
const markStateChanged = state => Functional.overProp('lastChangedAt', n => n + 1, state);

/*
 * Update a Loki collection and mark the state changed
 * Impure: updates Loki
 * @sig (State, String, *) -> State
 */
const updateLoki = (state, collection, value) => {
    LokiDb.upsert(collection, value);
    return markStateChanged(state);
};

const handlePerformValidation = (state, action) => {
    checkSpritesForVenue(action.venueId);

    return state;
};

/*
 * Redux reducer
 */
const reducer = (state = emptyState(), action) => {
    const a = actions;
    const LOADING = LOADING_STATUS_LOADING;
    const LOADED = LOADING_STATUS_LOADED;
    const FAILED = LOADING_STATUS_FAILED;

    switch (action.type) {
        case a.ADD_FEATURES:
            return updateLoki(state, LokiDb.FEATURES, action.features);
        case a.UPSERT_ENTITIES:
            return updateLoki(state, LokiDb.ENTITIES, action.entities);
        case a.SET_VENUE_SUMMARIES:
            return updateLoki(state, LokiDb.VENUE_SUMMARIES, action.venueSummaries);
        case a.SET_MAP_STYLE:
            return handleMapStyle(state, action);
        case a.SET_SPRITES:
            return handleSprites(state, action);
        case a.SET_VENUE_DROPDOWNS:
            return handleVenueDropdown(state, action);
        case a.SET_BASEMAP_BOUNDING_BOX:
            return handleSetBoundingBox(state, action);
        case a.SET_IS_MIGRATED:
            return handleSetIsMigrated(state, action);

        case a.RESET:
            return handleReset(state, action);
        case a.SET_THEME:
            return R.assoc('theme', action.theme, state);

        case a.SET_VENUE:
            return handleSetVenue(state, action);
        case a.SET_ORDINALS:
            return handleSetOrdinals(state, action);
        case a.SET_STRUCTURES:
            return handleSetStructures(state, action);
        case a.SET_LEVELS:
            return handleSetLevels(state, action);

        case a.REMOVE_FEATURE:
            return handleRemoveFeature(state, action);

        case a.SET_LOADING_STATUS:
            return loading(state, action.path, action.status);
        case a.VENUE_LOADING_STARTED:
            return loading(state, ['overallVenue'], LOADING);
        case a.VENUE_LOADING_FINISHED:
            return loading(state, ['overallVenue'], LOADED);
        case a.VENUE_LOADING_FAILED:
            return loading(state, ['overallVenue'], FAILED, ['overallVenueError'], action.error);
        case a.MAP_LOADING_STARTED:
            return loading(state, ['map'], LOADING);
        case a.MAP_LOADING_FINISHED:
            return loading(state, ['map'], LOADED);
        case a.MAP_LOADING_FAILED:
            return loading(state, ['map'], FAILED, ['mapError'], action.error);

        case a.PERFORM_VALIDATION:
            return handlePerformValidation(state, action);

        default:
            return state;
    }
};

export { reducer };
